import React, { useEffect, useRef, useState } from "react";
import "./SharedInvitations.css";
import DataBase from "../../../dataBase/liquidGuides";
import Common from "../../../Common/Common";
import { notifySharingChanged, onSharingChanged } from "../../../Common/useInvitationCount";
import { initialSharingState, normalizeSharingState, splitSharedGuides, declinedAndBlocked, otherHiddenIds, applySharingAction, personalShareLevel, shareLevelLabel } from "../../../Common/shareInvites";

// Library → Shared, top section (4.10.55, 2026-10-26): guides someone shared
// with you that you haven't accepted yet — View, Accept, Decline, Block.
// Loads the person's private sharing record and reports it to the Library
// through onStateChange, which then shows only accepted guides below.
// onStateChange(null) means the record can't be used (rules not published
// yet, or offline): the Library then shows every shared guide, as before.
// confirm(message, onYes) is the Library's own confirmation dialog.
// Below the invitations, "Declined and blocked (n)" (2026-10-29) opens a list
// to undo them: Restore makes a declined guide an invitation again, Unblock
// lets a person's guides show again. A hidden guide that isn't shared by name
// (a reported Featured guide) is read by id and listed there too (2026-11-01).
const SharedInvitations = ({ uid, guides, onStateChange, onOpen, confirm }) => {
  const [state, setState] = useState(null);
  const [showUndo, setShowUndo] = useState(false);
  const [others, setOthers] = useState([]);
  const loadedFor = useRef(null);

  // The Library's own "Remove from my Library" changes the record too.
  useEffect(() => onSharingChanged((st) => setState((cur) => (cur && st ? st : cur))), []);

  useEffect(() => {
    if (!uid || !guides || loadedFor.current === uid) return;
    loadedFor.current = uid;
    DataBase.getSharingState(uid, (res) => {
      if (res.pass !== true) {
        onStateChange(null);
        return;
      }
      let st = normalizeSharingState(res.res);
      if (!res.res || res.res.initialized !== true) {
        st = initialSharingState(guides, uid);
        DataBase.setSharingState(uid, st);
      }
      setState(st);
      onStateChange(st);
      notifySharingChanged(st);
    });
  }, [uid, guides, onStateChange]);

  const otherKey = state ? otherHiddenIds(guides, state).join(",") : "";
  useEffect(() => {
    if (!otherKey) return setOthers([]);
    let live = true;
    DataBase.getGuideInfosByIds(otherKey.split(",")).then((list) => live && setOthers(list));
    return () => (live = false);
  }, [otherKey]);

  if (!state) return null;
  const { invitations } = splitSharedGuides(guides, uid, state);
  const { declined, blocked } = declinedAndBlocked(guides, uid, state, others);
  const undoCount = declined.length + blocked.length;
  if (invitations.length === 0 && undoCount === 0) return null;

  const act = (action, id) => {
    const next = applySharingAction(state, action, id);
    setState(next);
    onStateChange(next);
    notifySharingChanged(next);
    DataBase.sharingAction(uid, action, id);
  };

  return (
    <>
      {invitations.length > 0 && (
        <div className="si-section">
          <div className="si-title">
            {invitations.length === 1 ? "1 invitation" : `${invitations.length} invitations`}
          </div>
          {invitations.map((g) => {
            const info = g.data;
            const from = info.owner_name || "Someone";
            const img = Common.getImageURL(info.image_m || info.image || "", null, false);
            return (
              <div className="si-card" key={g.id}>
                {img ? <img className="si-img" src={img} alt="" /> : <div className="si-img si-img--empty" />}
                <div className="si-meta">
                  <div className="si-name">{info.guide_name || "Untitled guide"}</div>
                  <div className="si-from">
                    {from} shared it with you · you {shareLevelLabel(personalShareLevel(info.access, uid))}
                  </div>
                  <div className="si-actions">
                    <button className="si-btn si-btn--primary" onClick={() => act("accept", g.id)}>Accept</button>
                    <button className="si-btn" onClick={() => onOpen(g)}>View</button>
                    <button className="si-btn" onClick={() => act("hide", g.id)}>Decline</button>
                    <button
                      className="si-btn si-btn--danger"
                      onClick={() =>
                        confirm(`Block ${from}? You won't see any guide they share with you, now or later. They aren't told.`, () =>
                          act("block", info.user_id)
                        )
                      }
                    >
                      Block
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
      {undoCount > 0 && (
        <div className="si-undo">
          <button className="si-undo-toggle" onClick={() => setShowUndo(!showUndo)} aria-expanded={showUndo}>
            {showUndo ? "▾" : "▸"} Declined and blocked ({undoCount})
          </button>
          {showUndo && (
            <div className="si-undo-list">
              {declined.map((g) => (
                <div className="si-undo-row" key={g.id}>
                  <div className="si-undo-text">
                    <div className="si-name">{g.data.guide_name || "Untitled guide"}</div>
                    <div className="si-from">from {g.data.owner_name || "someone"} · {g.other ? "hidden" : "declined"}</div>
                  </div>
                  <button className="si-btn" onClick={() => act("restore", g.id)}>Restore</button>
                </div>
              ))}
              {blocked.map((p) => (
                <div className="si-undo-row" key={p.uid}>
                  <div className="si-undo-text">
                    <div className="si-name">{p.name || "Someone you blocked"}</div>
                    <div className="si-from">
                      blocked · {p.guides === 0 ? "no guides shared with you now" : p.guides === 1 ? "1 guide shared with you" : `${p.guides} guides shared with you`}
                    </div>
                  </div>
                  <button className="si-btn" onClick={() => act("unblock", p.uid)}>Unblock</button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default SharedInvitations;
